import { EditorErrorBoundary, suppressErrors } from "@prismicio/editor-fields"
import { FatalError } from "@prismicio/editor-support/Error"
import { isAuthError, isRequestError } from "@prismicio/editor-support/Http"
import { cache } from "@prismicio/editor-support/Suspense"
import { Box, DefaultErrorMessage, ErrorBoundary } from "@prismicio/editor-ui"
import { captureException } from "@sentry/react"
import { useQueryClient } from "@tanstack/react-query"
import { type PropsWithChildren, useRef, useState } from "react"
import { isRouteErrorResponse, Navigate, useRouteError } from "react-router-dom"

import { useGoToOldDocumentList } from "../../document/documentRouting"
import { LoginForm } from "../auth/Login"
import { env } from "../env"
import {
  clearExperiments,
  documentListFlag
} from "../integrations/AmplitudeExperiments"
import { shutdownIntercom } from "../integrations/intercom"
import { isParamsValidationError } from "../routing"

export function AppErrorHandler({ children }: PropsWithChildren) {
  const resetErrorBoundaryRef = useRef(() => {})
  const [showLogin, setShowLogin] = useState(false)
  const queryClient = useQueryClient()

  function interceptError(error: unknown, reset: () => void) {
    resetErrorBoundaryRef.current = reset

    if (!isAuthError(error)) {
      if (shouldCaptureError(error)) captureException(error)
      return false
    }

    shutdownIntercom()
    clearExperiments()

    if (env.PAGE_BUILDER_AUTH_STRATEGY === "token") {
      // Wait for the Error Boundary to finish rendering before we show the login screen.
      setTimeout(() => setShowLogin(true))
    } else {
      window.location.href = window.location.origin
    }
    return true
  }

  function onLoggedIn() {
    // Start from a blank slate; also because the provided credentials
    // might be different and returned data may vary.
    cache.clearAll()
    queryClient.clear()
    resetErrorBoundaryRef.current()

    setShowLogin(false)
  }

  // No side effects. This needs to be done before we render our children
  // as they could throw during their first render.
  ErrorBoundary.registerGlobalErrorInterceptor(interceptError)

  return (
    <>
      <EditorErrorBoundary renderError={renderError}>
        {children}
      </EditorErrorBoundary>
      <LoginForm shown={showLogin} onLoggedIn={onLoggedIn} />
    </>
  )
}

function renderError() {
  return (
    <Box flexDirection="column" width="100%" padding={80}>
      <DefaultErrorMessage title="Prismic has encountered an error" />
    </Box>
  )
}

/*
 * All rendering app errors (including Suspense) will bubble up to this JSX handler
 * after being caught by react-router's own Error Boundary.
 * If the error can be recovered from automatically, we do so,
 * otherwise we let our own ErrorBoundary and its manual Retry Button take over.
 */
export function RouterError() {
  const error = useRouteError()
  const goToOldDocumentList = useGoToOldDocumentList()

  if (isRouteErrorResponse(error) || isParamsValidationError(error)) {
    if (import.meta.env.DEV) console.error(error)

    if (documentListFlag() === "on") {
      return <Navigate to="/" replace />
    }

    goToOldDocumentList()
    return null
  }

  throw error
}

function shouldCaptureError(error: unknown) {
  // In general, we assume our HTTP endpoints are well configured in the frontend
  // and any error should be reported from the backend systems instead.
  if (isRequestError(error)) return false

  // We throw a FatalError when the error can not be recovered from by clicking "try again".
  // There's nothing to report as this represents an expected/valid Page Builder state.
  if (error instanceof FatalError) return false

  return true
}

suppressErrors()
